import { TFunction } from 'i18next'
import { ZodCustomIssue, ZodIssue } from 'zod'

/**
 * Type guard to check if an error object contains Zod validation issues.
 * Used to safely extract validation errors from API responses.
 */
export function hasZodIssues(error: unknown): error is { issues: ZodIssue[] } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'issues' in error &&
    Array.isArray((error as { issues?: unknown }).issues) &&
    (error as { issues: unknown[] }).issues.length > 0
  )
}

/**
 * Safely extracts Zod issues from an error object.
 * Returns undefined if the error doesn't contain valid Zod issues.
 */
export function extractZodIssues(error: unknown): ZodIssue[] | undefined {
  return hasZodIssues(error) ? error.issues : undefined
}

/**
 * The business-rule failures in a thrown error body, as opposed to the field-level ones.
 *
 * A backend rule that no single field can express is reported as a custom issue carrying a
 * `params.type` — the key its wording is translated from. Everything else in the body describes one
 * field being wrong, which {@link mapZodErrorsToMessages} already speaks for.
 */
export function extractCustomZodIssues(error: unknown): ZodCustomIssue[] {
  return extractZodIssues(error)?.filter((issue) => issue.code === 'custom') ?? []
}

type CustomIssueTranslation = {
  /** The i18n namespace the wording lives in. */
  ns: string
  /** What precedes the issue's own type in the key, so `params.type` is the last segment. */
  keyPrefix: string
  /** Shown when the backend named a type this side has no wording for. Already translated. */
  fallbackDescription: string
}

/**
 * What one business-rule failure should read as, translated.
 *
 * The fallback arrives already translated rather than as a key, so a caller whose fallback lives in
 * another namespace than the failure's own wording can pass it without this having to know two of
 * them — the `getPublishErrorDescription` pattern.
 *
 * Everything in `params` besides `type` is forwarded as interpolation values, which is how a rule
 * that reports figures alongside itself gets them into its sentence.
 */
export function getTranslatedCustomIssueDescription(
  issue: ZodCustomIssue,
  t: (key: string, options?: Record<string, unknown>) => string,
  { ns, keyPrefix, fallbackDescription }: CustomIssueTranslation,
): string {
  const { type, ...values } = issue.params ?? {}
  if (typeof type !== 'string') return fallbackDescription

  return t(`${keyPrefix}.${type}`, { ns, ...values, defaultValue: fallbackDescription })
}

/**
 * Maps Zod validation errors to translated error messages.
 */
export function mapZodErrorsToMessages(
  issues: ZodIssue[],
  t: TFunction,
  namespace: string = 'common',
) {
  return issues.map((issue) => {
    const field = issue.path.join('.')
    // Labels are keyed by the field's own name: `updates.0.price` reads `fields.price`. Looked up only
    // where a message uses it, so other issues on unlabelled fields don't report a missing key.
    const fieldName = issue.path.findLast((segment) => typeof segment === 'string')
    const getFieldLabel = () =>
      fieldName ? t(`fields.${fieldName}`, { ns: namespace, defaultValue: fieldName }) : ''

    let message: string

    switch (issue.code) {
      case 'invalid_type':
        if (issue.received === 'undefined' || issue.received === 'null') {
          message = t('errors.validation.required', { ns: 'common', field: getFieldLabel() })
        } else {
          message = t('errors.validation.invalid_type', {
            ns: 'common',
            expected: issue.expected,
            received: issue.received,
          })
        }
        break

      case 'too_small':
        if (issue.type === 'string') {
          message = t('errors.validation.too_small.string', {
            ns: 'common',
            minimum: issue.minimum,
          })
        } else if (issue.type === 'number') {
          message = t('errors.validation.too_small.number', {
            ns: 'common',
            minimum: issue.minimum,
          })
        } else {
          message = t('errors.validation.too_small.default', {
            ns: 'common',
            minimum: issue.minimum,
          })
        }
        break

      case 'too_big':
        if (issue.type === 'string') {
          message = t('errors.validation.too_big.string', {
            ns: 'common',
            maximum: issue.maximum,
          })
        } else if (issue.type === 'number') {
          message = t('errors.validation.too_big.number', {
            ns: 'common',
            maximum: issue.maximum,
          })
        } else {
          message = t('errors.validation.too_big.default', {
            ns: 'common',
            maximum: issue.maximum,
          })
        }
        break

      case 'invalid_enum_value':
        message = t('errors.validation.invalid_enum_value', {
          ns: 'common',
          options: issue.options.join(', '),
        })
        break

      case 'invalid_string':
        if (issue.validation === 'email') {
          message = t('errors.validation.invalid_email', { ns: 'common' })
        } else if (issue.validation === 'url') {
          message = t('errors.validation.invalid_url', { ns: 'common' })
        } else {
          message = t('errors.validation.invalid_string', { ns: 'common' })
        }
        break

      case 'invalid_date':
        message = t('errors.validation.invalid_date', { ns: 'common' })
        break

      default:
        // Fallback to custom message if provided, or generic error
        message = issue.message || t('errors.validation.invalid', { ns: 'common' })
    }

    return {
      field,
      message,
      code: issue.code,
    }
  })
}

/**
 * Gets the first error message from Zod issues, or a fallback message.
 */
export function getFirstZodErrorMessage(
  issues: ZodIssue[] | undefined,
  t: TFunction,
  fallbackKey: string,
  namespace: string = 'common',
): string {
  if (!issues || issues.length === 0) {
    return t(fallbackKey, { ns: namespace })
  }

  const errors = mapZodErrorsToMessages(issues, t, namespace)
  return errors[0]?.message || t(fallbackKey, { ns: namespace })
}

/**
 * Gets error messages grouped by field from Zod issues.
 */
export function getZodErrorsByField(
  issues: ZodIssue[],
  t: TFunction,
  namespace: string = 'common',
): Map<string, string> {
  const errors = mapZodErrorsToMessages(issues, t, namespace)
  const errorMap = new Map<string, string>()

  for (const error of errors) {
    if (!errorMap.has(error.field)) {
      errorMap.set(error.field, error.message)
    }
  }

  return errorMap
}
